import clsx from 'clsx'

import { sanitizeEmbedHtml, sanitizeHtml } from '@/shared/lib/html/sanitize'
import { safeHref } from '@/shared/lib/url/safe-href'
import Image from '@/shared/ui/AppImage'
import { Button } from '@/shared/ui/Button'
import type { GamificationPageBlock } from '@/types/gamificationPageBlock'

interface BlockProps {
  block: GamificationPageBlock
}

const linkTarget = (block: GamificationPageBlock) =>
  block.settings?.target === '_blank' ? '_blank' : '_self'

const relFor = (target: string) =>
  target === '_blank' ? 'noopener noreferrer' : undefined

/** Imagem com link. `media_id` é FK real na api — não vive dentro de jsonb. */
export const BannerBlock = ({ block }: BlockProps) => {
  const url = block.media?.file?.url
  if (!url) return null

  const ratio = block.settings?.ratio ?? '16/9'
  const target = linkTarget(block)

  const image = (
    <Image
      src={url}
      alt={block.title ?? ''}
      width={1200}
      height={675}
      className="h-full w-full rounded-xl object-cover"
    />
  )

  const href = safeHref(block.external_link)

  return (
    <div className="overflow-hidden" style={{ aspectRatio: ratio }}>
      {href ? (
        <a
          href={href}
          target={target}
          rel={relFor(target)}
          className="block h-full w-full"
        >
          {image}
        </a>
      ) : (
        image
      )}
    </div>
  )
}

/** Sanitizado também na gravação, na api. Aqui é a segunda barreira. */
export const RichTextBlock = ({ block }: BlockProps) => {
  if (!block.text_content) return null

  return (
    <div
      className="wysiwyg-content text-base text-gray-900"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.text_content) }}
    />
  )
}

/** Host do `src` é validado contra allowlist na api antes de gravar. */
export const EmbedBlock = ({ block }: BlockProps) => {
  if (!block.embed_code) return null

  const ratio = block.settings?.ratio ?? '16/9'

  return (
    <div
      className="overflow-hidden rounded-xl [&_iframe]:h-full [&_iframe]:w-full"
      style={{ aspectRatio: ratio }}
      dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(block.embed_code) }}
    />
  )
}

export const CtaBlock = ({ block }: BlockProps) => {
  const href = safeHref(block.external_link)
  if (!block.title || !href) return null

  const target = linkTarget(block)

  return (
    <a
      href={href}
      target={target}
      rel={relFor(target)}
      className="block no-underline"
    >
      <Button
        variant={(block.settings?.variant as any) ?? 'primary'}
        size="lg"
        className="w-full"
      >
        {block.title}
      </Button>
    </a>
  )
}

export const SectionTitleBlock = ({ block }: BlockProps) => {
  if (!block.title) return null

  const size = block.settings?.size ?? 'md'

  return (
    <h2
      className={clsx(
        'm-0 font-bold leading-tight text-gray-900',
        size === 'sm' && 'text-base',
        size === 'md' && 'text-lg',
        size === 'lg' && 'text-xl'
      )}
    >
      {block.title}
    </h2>
  )
}
